import { useMemo } from 'react'
import { useParams } from 'react-router'
import { Async } from '../../platform/async/Async'
import { Skeleton } from '../../platform/async/Skeleton'
import { InlineLink } from '../../platform/primitives/InlineLink'
import { Panel } from '../../platform/primitives/Panel'
import { MerchantAvatar } from '../../platform/primitives/MerchantAvatar'
import { CategoryChip } from '../../platform/primitives/CategoryChip'
import { DataGrid, type Column } from '../../platform/primitives/DataGrid'
import { Money } from '../../platform/primitives/Money'
import { useCategories } from '../categories/useCategories'
import { useMerchant, useMerchantTransactions, type Merchant, type MerchantTransaction } from './useMerchants'
import './merchantTransactions.css'

/** Same five values, same labels, as `Merchants.tsx`'s own — repeated rather
 *  than imported: that map lives in a file Task 3's owner is still editing,
 *  and this screen has nothing else to import from it. */
const TYPE_LABEL: Record<Merchant['type'], string> = {
  business: 'Business',
  person: 'Person',
  contact: 'Contact',
  service: 'Service',
  other: 'Other',
}

/** en-GB, matching `Async`'s own stale-date formatting — the one other place
 *  this app renders a bare date today. */
const DATE = new Intl.DateTimeFormat('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })

/** A non-empty string, trimmed — the same "word, not a blank" test
 *  `Merchants.tsx`'s own `hasText` applies, repeated rather than imported for
 *  the same reason every small route-local helper in this module is: no
 *  shared home either file would agree belongs to it more than its own. */
function hasText(s: string | null | undefined): s is string {
  return s != null && s.trim() !== ''
}

/** `personal_finance_category_primary` arrives upstream-shaped —
 *  `FOOD_AND_DRINK`, `GENERAL_MERCHANDISE` — for a machine, not a reader.
 *  This is the one place that value is ever shown, so the humanising lives
 *  here rather than in a shared formatter nothing else calls. */
function humanizeProviderCategory(value: string): string {
  const lower = value.replace(/_/g, ' ').toLowerCase()
  return lower.charAt(0).toUpperCase() + lower.slice(1)
}

/**
 * `/merchants/:id/transactions` — reached only from a row's expanded action
 * bar's "See transactions" (`Merchants.tsx`), never from a nav item.
 *
 * **Replaces Task 11's `/merchants/:id`, not just a rename.** The design
 * that arrived 2026-08-21 has no detail SCREEN — a row expands in place for
 * its identity, `website`, `notes` and its own logo control (all now in
 * `Merchants.tsx`'s `MerchantDetailPanel`). What is left here is the one
 * thing that genuinely needs its own route: a merchant's transactions,
 * which are their own paged resource, not a handful of fields that fit
 * inside a table row.
 *
 * **No stats block.** M11-R6 wants This year / Visits / Average, and
 * `GET /transactions/summary` accepts only `start_date`, `end_date`,
 * `account_id` — nothing that scopes a summary to a merchant. The block is
 * absent, not disabled or stubbed. Filed as #295.
 *
 * **The back link and the identity header are page-level, outside
 * `<Panel>`** (#302's fix) — the same split `Merchants.tsx` already draws
 * between its own header/toolbar (on the canvas) and the table (inside
 * `Panel`). The earlier version put the back link, the identity block AND
 * the transactions grid inside one `<Panel>` together: `.surface-box`'s own
 * `flex-direction: column` defaults to `align-items: stretch`, which
 * stretched `InlineLink` — an `inline-flex` with its own `justify-content:
 * center` for centring short LABEL text inside a small hit-target box, not
 * for centring itself inside its container — to the panel's full width, so
 * the link rendered dead centre instead of at its own natural, left-aligned
 * width. Moving it outside `Panel` removes the stretching ancestor rather
 * than fighting it with a second declaration.
 *
 * `useParams()`, not `useParams<{ id: string }>()` — `CategoryDetail.tsx`
 * already settled this for the app: the router types a param as possibly
 * absent, and a generic asserting it away just moves the lie.
 */
export function MerchantTransactions() {
  const { id } = useParams()
  const state = useMerchant(id)

  return (
    <>
      {/* The one way back — this screen has no nav entry of its own, so a
          user who followed "See transactions" from an expanded row has no
          other route to `/merchants` without it. "← Back to X" is the same
          copy `ForgotPassword.tsx`/`ResetPassword.tsx` already use for their
          own return links — the words carry the accessible name regardless
          of whether a screen reader announces the arrow glyph, so this stays
          plain text rather than inventing a second treatment. `InlineLink`
          for a standalone link (CLAUDE.md rule 11). Rendered unconditionally,
          above `<Async>`, so it stays reachable while the merchant itself is
          still loading or has failed to. */}
      {/* The wrapping `<div>`, not a className on `InlineLink` itself, carries
          the spacing below. `compositeContrast.test.tsx` pins the bare
          `a.inline-link.hoverable` signature's known 4.05:1 accent-ink
          reading on the washed page composite (the same one
          `StatesReference`'s own bare `InlineLink` calls already produce,
          `KNOWN_PAGE_BACKGROUND_FAILURES`'s own row) — an extra class here
          would change that selector string and turn one already-accepted
          finding into what looks like a second, unpinned one. */}
      <div className="merchant-transactions__back">
        <InlineLink to="/merchants">← Back to merchants</InlineLink>
      </div>
      <Async
        surface="merchants"
        state={state}
        skeleton={
          <>
            <Skeleton height={64} width={64} />
            <Skeleton height={28} width={180} />
            <Skeleton height={16} />
            <Skeleton height={16} />
          </>
        }
        // Unreachable in practice — `useMerchant` passes no `isEmpty`, so
        // `fromQuery` never returns this status for a single-resource fetch.
        // `<Async>`'s own `empty` prop is required regardless, the same way
        // `DataGrid`'s is: a state nothing today produces is still one the
        // type admits, and the title should read sensibly if it ever fires.
        empty={{ title: 'Merchant not found' }}
      >
        {(merchant) => <MerchantTransactionsBody merchant={merchant} />}
      </Async>
    </>
  )
}

function MerchantTransactionsBody({ merchant }: { merchant: Merchant }) {
  const txState = useMerchantTransactions(merchant.id)

  // A second, independent observer of the categories cache — never a second
  // fetch (`useCategories` shares one `queryKey` across every caller, its own
  // docblock). This grid's own loading/error/empty states are never gated on
  // it: a category that has not resolved yet (still loading, or the request
  // failed) falls through to the row's "Uncategorized" branch below rather
  // than blocking a surface that is otherwise ready to show.
  const categoriesState = useCategories()
  const categoryById = useMemo(() => {
    // No `color` field — #454 removed it, and `CategoryChip` below no longer
    // reads one.
    const map = new Map<string, { name: string }>()
    if (categoriesState.status === 'success') {
      for (const c of categoriesState.data) map.set(c.id, { name: c.name })
    }
    return map
  }, [categoriesState])

  const columns: Column<MerchantTransaction>[] = [
    {
      key: 'date',
      header: 'Date',
      width: 'minmax(0, 0.8fr)',
      render: (t) => (
        <span className="merchant-transactions__tx-date">
          {DATE.format(new Date(`${t.date}T00:00:00`))}
          {/* Not colour alone (M0-R11) — the WORD is the signal, `--warn`
              only reinforces it. Same `--warn-soft`/`--warn` pairing
              `errors.css`'s own `.maint-badge` already proves. */}
          {t.isPending && <span className="merchant-transactions__pending">Pending</span>}
        </span>
      ),
    },
    {
      key: 'name',
      header: 'Description',
      width: 'minmax(0, 1.8fr)',
      render: (t) => (
        <span className="merchant-transactions__tx-cell">
          <span className="merchant-transactions__tx-name">{t.name}</span>
          {/* The raw string the bank/provider supplied — explicitly wanted
              beneath the transaction's own (possibly cleaned-up) name, in a
              monospace face, muted: it reads as DATA rather than as a second
              sentence. Only rendered when the provider actually sent one. */}
          {hasText(t.merchantName) && (
            <span className="merchant-transactions__tx-raw">{t.merchantName}</span>
          )}
          {/* `payment_channel`/`location_city` — chosen over the pair's own
              other half (`location_region`) because one short meta line
              reads cleanly beneath two lines of name/descriptor already, and
              a THIRD line (city plus region) started to crowd the row in a
              hand check of the rendered markup. Channel alone is also the
              more consistently-populated of the two upstream fields; city is
              added only when the provider actually sent one. */}
          {(hasText(t.paymentChannel) || hasText(t.locationCity)) && (
            <span className="merchant-transactions__tx-meta">
              {[
                hasText(t.paymentChannel) ? humanizeProviderCategory(t.paymentChannel) : null,
                hasText(t.locationCity) ? t.locationCity : null,
              ]
                .filter(hasText)
                .join(' · ')}
            </span>
          )}
        </span>
      ),
    },
    {
      key: 'category',
      header: 'Category',
      width: 'minmax(0, 1fr)',
      render: (t) => {
        const category = t.categoryId ? categoryById.get(t.categoryId) : undefined
        if (category) return <CategoryChip name={category.name} size="row" />
        if (hasText(t.personalFinanceCategoryPrimary)) {
          return (
            <span className="merchant-transactions__tx-provider-category">
              {humanizeProviderCategory(t.personalFinanceCategoryPrimary)}
            </span>
          )
        }
        return <span className="merchant-transactions__tx-uncategorised">Uncategorized</span>
      },
    },
    {
      key: 'amount',
      header: 'Amount',
      width: 'minmax(0, 0.7fr)',
      align: 'end',
      // `kind="flow"` — a transaction amount signs by direction, the same
      // treatment `DataGrid`'s own group subtotal takes. `currency` is the
      // transaction's OWN `iso_currency_code`, not `Money`'s CAD default —
      // parsed once, at the API boundary, in `useMerchants.ts`.
      render: (t) => <Money cents={t.amountCents} currency={t.currency} kind="flow" />,
    },
  ]

  return (
    <>
      <div className="merchant-transactions__header">
        <MerchantAvatar merchant={merchant} size="detail" />
        <div className="merchant-transactions__heading">
          <h2 className="merchant-transactions__title">{merchant.name}</h2>
          <span className="merchant-transactions__type">{TYPE_LABEL[merchant.type]}</span>
        </div>
      </div>

      <Panel>
        {/* Architect I8. The fetch asks for 25 and the screen showed no sign
            of it, so a merchant with 300 transactions rendered 25 and looked
            complete — a page silently showing a subset is making a false claim
            about completeness, the same class of dishonesty the delete copy
            was fixed for.

            The count comes from `total`, which the response already carries,
            so this needs no second request and no new control. The cap is only
            named when it actually bites: "Transactions · 25 of 300" when there
            is more, and a plain "Transactions" when there is not, rather than
            "25 of 4" on every short list. A pager here would be better and is
            #305. */}
        <h3 className="merchant-transactions__section-title">
          Transactions
          {txState.status === 'success' && txState.data.total > txState.data.transactions.length && (
            <span className="merchant-transactions__section-count">
              {' · '}
              {txState.data.transactions.length} of {txState.data.total}
            </span>
          )}
        </h3>
        <Async
          surface="merchant-transactions"
          state={txState}
          skeleton={
            <>
              <Skeleton height={44} />
              <Skeleton height={44} />
              <Skeleton height={44} />
            </>
          }
          empty={{ title: 'No transactions for this merchant yet.' }}
        >
          {(page) => (
            <DataGrid
              columns={columns}
              groups={[{ label: '', rows: page.transactions }]}
              rowKey={(t) => t.id}
              label="Transactions"
              empty="No transactions on this page."
            />
          )}
        </Async>
      </Panel>
    </>
  )
}
